import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { buildingHomeContentApi } from '../../api/buildingHomeContentApi';
import { ImageUploadField } from '../../components/common/ImageUploadField';

export const AddEditBanner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    buildingId: user?.buildingId || '',
    title: '',
    description: '',
    imageUrl: '',
    linkUrl: '',
    displayOrder: 0,
    isActive: true,
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    loadBuildings();
    if (isEdit && id) {
      loadBanner(id);
    }
  }, [id]);

  const loadBuildings = async () => {
    const response = await buildingsApi.getBuildings();
    if (Array.isArray(response)) {
      setBuildings(response);
      if (!isEdit && !formData.buildingId) {
        const defaultId = user?.buildingId || response[0]?.id || '';
        if (defaultId) {
          setFormData((prev) => ({ ...prev, buildingId: defaultId }));
        }
      }
    }
  };

  const loadBanner = async (bannerId: string) => {
    try {
      const banner = await buildingHomeContentApi.getBanner(bannerId);
      setFormData({
        buildingId: banner.buildingId,
        title: banner.title,
        description: banner.description || '',
        imageUrl: banner.imageUrl || '',
        linkUrl: banner.linkUrl || '',
        displayOrder: banner.displayOrder,
        isActive: banner.isActive,
        startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
        endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
      });
    } catch {
      alert('Không thể tải banner');
      navigate('/building-banners');
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!formData.buildingId) {
      alert('Vui lòng chọn tòa nhà trước khi upload ảnh');
      return;
    }

    try {
      setUploading(true);
      const result = await buildingHomeContentApi.uploadImage(file, formData.buildingId, 'banners');
      setFormData((prev) => ({ ...prev, imageUrl: result.url }));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Upload ảnh thất bại');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.buildingId || !formData.title.trim()) {
      alert('Vui lòng nhập tiêu đề và chọn tòa nhà');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        imageUrl: formData.imageUrl.trim(),
        linkUrl: formData.linkUrl.trim(),
        displayOrder: Number(formData.displayOrder) || 0,
        isActive: formData.isActive,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
      };

      if (isEdit && id) {
        await buildingHomeContentApi.updateBanner(id, payload);
      } else {
        await buildingHomeContentApi.createBanner({
          buildingId: formData.buildingId,
          title: payload.title,
          description: payload.description || undefined,
          imageUrl: payload.imageUrl || undefined,
          linkUrl: payload.linkUrl || undefined,
          displayOrder: payload.displayOrder,
          isActive: payload.isActive,
          startDate: formData.startDate || undefined,
          endDate: formData.endDate || undefined,
        });
      }
      navigate('/building-banners');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Lưu banner thất bại');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-8">
      <nav className="text-sm text-gray-500 mb-4">
        <Link to="/building-banners" className="hover:text-primary-600">Quản lý banner</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{isEdit ? 'Sửa banner' : 'Thêm banner'}</span>
      </nav>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">{isEdit ? 'Sửa banner' : 'Thêm banner'}</h1>
      <p className="text-gray-600 mb-8">Banner hiển thị trên trang chủ ứng dụng mobile</p>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tòa nhà</label>
          <select
            value={formData.buildingId}
            onChange={(e) => setFormData((prev) => ({ ...prev, buildingId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
            disabled={isEdit}
          >
            <option value="">-- Chọn tòa nhà --</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mô tả</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
        </div>

        <ImageUploadField
          label="Ảnh banner"
          imageUrl={formData.imageUrl}
          uploading={uploading}
          disabled={!formData.buildingId}
          onUpload={handleImageUpload}
          hint={!formData.buildingId ? 'Chọn tòa nhà trước khi upload ảnh' : 'JPG, PNG, WEBP. Tối đa 10MB'}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Liên kết (URL)</label>
          <input
            type="url"
            value={formData.linkUrl}
            onChange={(e) => setFormData((prev) => ({ ...prev, linkUrl: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="https://..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Thứ tự hiển thị</label>
            <input
              type="number"
              min={0}
              value={formData.displayOrder}
              onChange={(e) => setFormData((prev) => ({ ...prev, displayOrder: Number(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center pt-6">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Hiển thị trên app</label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ngày bắt đầu</label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ngày kết thúc</label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-md disabled:opacity-50"
          >
            {loading ? 'Đang lưu...' : 'Lưu'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/building-banners')}
            className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Hủy
          </button>
        </div>
      </form>
    </div>
  );
};
